/**
 * 「レイアウトを再利用」(生成情報モーダル・docs/features/banner-layout-template-plan.md §4 / §8 段階2)。
 *
 * 生成した素材の generation_metadata.banner_layout から settings.banner_layout と比率を明示的に復元する
 * (AI修正依頼はレイアウトを引き継がない。それとは別の入口)。文言はレイアウトに持たない (「テキスト」欄の値を使う・§13)。
 * 商品枠は「選択中の画像の何枚目か」(imageIndex) を指すので、今の選択中の画像で自動的に解決される (§17)。
 * 旧記録に残る assetId は、その素材が今も選択中なら優先され、外れていれば何枚目かで解決される (素材が無ければ BE が枠を外す)。
 */
import type { GenerationSettings } from '../../components/generation/state/types';
import { effectiveAspectRatio, ratiosMatch } from './effectiveAspectRatio';
import { stripLayoutTexts } from './textSync';
import { bannerLayoutSchema, type BannerLayout } from './types';

/** generation_metadata.banner_layout → 再利用するレイアウト (記録用の dropped / shared_material_id は落とす)。不正なら null */
export function layoutFromGenerationMetadata(raw: unknown): BannerLayout | null {
  if (!raw || typeof raw !== 'object') return null;
  const { version, aspectRatio, elements, background } = raw as Record<string, unknown>;
  // 背景の分割 (§16) も復元する。要素の傾き (rotation) は elements に入っている
  const parsed = bannerLayoutSchema.safeParse({ version, aspectRatio, elements, ...(background ? { background } : {}) });
  // 文言はレイアウトに持たない (段階1の生成記録に残る text は外す・§13)
  return parsed.success ? { ...parsed.data, elements: stripLayoutTexts(parsed.data.elements) } : null;
}

/**
 * レイアウトを適用する settings の差分。実効比率がレイアウトとずれるときは、比率をレイアウトに合わせ、
 * 比率を固定している媒体規格・目標解像度を外す (ずれたままだと生成ボタンが止まるため)。
 */
export function reuseLayoutSettings(settings: GenerationSettings, layout: BannerLayout): Partial<GenerationSettings> {
  const patch: Partial<GenerationSettings> = { banner_layout: layout, banner_layout_name: null };
  if (!ratiosMatch(layout.aspectRatio, effectiveAspectRatio(settings))) {
    Object.assign(patch, {
      aspect_ratio: layout.aspectRatio,
      media_platform_id: null,
      media_menu_id: null,
      media_spec_id: null,
      media_guidelines: [],
      media_menu_guidelines: [],
      auto_trim_resolution: false,
      target_width: undefined,
      target_height: undefined,
    } satisfies Partial<GenerationSettings>);
  }
  if (layout.elements.some((e) => e.kind === 'logo')) patch.include_logo = true;
  return patch;
}
